// src/components/ThreatSearchView.js
import React, { useState } from 'react';
import { Search, Globe, Hash, Mail, FileText, Database, Filter, Download } from 'lucide-react';

const ThreatSearchView = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const searchTypes = [
    { value: 'all', label: 'All Sources', icon: Database },
    { value: 'domain', label: 'Domain', icon: Globe },
    { value: 'ip', label: 'IP Address', icon: Hash },
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'hash', label: 'File Hash', icon: FileText }
  ];

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    // Placeholder for actual search implementation
    setTimeout(() => {
      setSearchResults([
        {
          id: 1,
          type: 'Domain',
          value: searchQuery,
          threat_level: 'High',
          first_seen: '2024-01-15',
          sources: ['Phishing Feed', 'Threat Actors'],
          related_incidents: 2
        }
      ]);
      setSearching(false);
    }, 1000);
  };

  return (
    <div className="h-full flex flex-col">
      {/* HEADER */}
      <div className="px-8 py-6 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30">
        <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-3 mb-2">
          <Search className="w-7 h-7 text-cyan-400" />
          Threat Intelligence Search
        </h2>
        <p className="text-sm text-slate-400">
          Search across all threat intelligence sources including domains, IPs, malware, actors, and IOCs
        </p>
      </div>

      {/* SEARCH INTERFACE */}
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-4xl mx-auto">
          {/* Search Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-3">Search Type</label>
            <div className="grid grid-cols-5 gap-2">
              {searchTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    onClick={() => setSearchType(type.value)}
                    className={`p-3 rounded-lg border transition-all ${
                      searchType === type.value
                        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                        : 'bg-slate-800/30 border-slate-700/50 text-slate-400 hover:bg-slate-800/50'
                    }`}
                  >
                    <Icon className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs font-medium">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-3">Search Query</label>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Enter domain, IP, hash, email, or keyword..."
                  className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={!searchQuery.trim() || searching}
                className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-all"
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          <div className="mb-8">
            <button className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
              <Filter className="w-4 h-4" />
              Advanced Filters
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-100">
                  Search Results ({searchResults.length})
                </h3>
                <button className="flex items-center gap-2 px-3 py-2 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 rounded-lg text-sm text-slate-300 transition-all">
                  <Download className="w-4 h-4" />
                  Export Results
                </button>
              </div>

              {searchResults.map((result) => (
                <div key={result.id} className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6 hover:border-cyan-500/30 transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 bg-slate-700/50 rounded-lg text-sm text-slate-300">
                          {result.type}
                        </span>
                        <span className={`px-3 py-1 rounded-lg text-sm border ${
                          result.threat_level === 'High' 
                            ? 'bg-red-500/20 text-red-400 border-red-500/30'
                            : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                        }`}>
                          {result.threat_level} Risk
                        </span>
                      </div>
                      <h4 className="text-lg font-semibold text-slate-100 font-mono">{result.value}</h4>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                    <div>
                      <span className="text-slate-500">First Seen:</span>
                      <span className="ml-2 text-slate-200">
                        {new Date(result.first_seen).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Related Incidents:</span>
                      <span className="ml-2 text-slate-200">{result.related_incidents}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Sources:</span>
                      <span className="ml-2 text-slate-200">{result.sources.length}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {result.sources.map((source, idx) => (
                      <span key={idx} className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs border border-purple-500/30">
                        {source}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {searchResults.length === 0 && !searching && (
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-12 text-center">
              <Search className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-300 mb-2">No Results Yet</h3>
              <p className="text-sm text-slate-500">
                Enter a search query above to find threats across all intelligence sources
              </p>
            </div>
          )}

          {/* Loading State */}
          {searching && (
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-12 text-center">
              <div className="animate-spin w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-sm text-slate-400">Searching threat intelligence databases...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThreatSearchView;